import type { BotTier } from '@runkr/config'
import { nextInt, nextRange } from '../rng'
import type { Rng } from '../rng'
import { NO_PERSONALITY, NO_TICK } from '../world'
import type { BotMemory } from '../world'

// Bot variety (T1.4b, D-031): what makes four tier-1 bots with the same logic act apart. Since
// D-045 everyone runs at one speed, so the staggered start keeps runners apart and these draws
// only vary timing and mistakes. A one-value range or a 0 % / 100 % chance draws nothing, so a
// tier without variety consumes exactly the randomness T1.4 did.

/** `nextRange`, without a draw when `lo === hi`. */
export function drawRange(rng: Rng, lo: number, hi: number): number {
  return lo === hi ? lo : nextRange(rng, lo, hi)
}

/** True with `pct` percent probability (no draw at 0 or 100). */
export function chance(rng: Rng, pct: number): boolean {
  if (pct <= 0) return false
  if (pct >= 100) return true
  return nextInt(rng, 100) < pct
}

/** The bot's personality, drawn once on its first `botInput` call: its upper-route chance. */
export function ensurePersonality(mem: BotMemory, tier: BotTier, rng: Rng): void {
  if (mem.upperRoutePct !== NO_PERSONALITY) return
  mem.upperRoutePct = drawRange(rng, tier.upperRouteMinPct, tier.upperRouteMaxPct)
}

/**
 * Drawn with each new obstacle's reaction delay: how many ticks early the bot acts (only where
 * acting that early still clears) and, with `lateChancePct`, a late slip past the last safe tick.
 */
export function drawObstacleTiming(mem: BotMemory, tier: BotTier, rng: Rng): void {
  mem.actEarlyTicks = drawRange(rng, 0, tier.actEarlyMaxTicks)
  mem.lateTicks = chance(rng, tier.lateChancePct)
    ? drawRange(rng, tier.lateMinTicks, tier.lateMaxTicks)
    : 0
  clearSlip(mem)
}

/** Drop a pending late slip. */
export function clearSlip(mem: BotMemory): void {
  mem.slipAtTick = NO_TICK
  mem.slipAction = 'none'
}
